import { z } from "zod";
import { personaStatusEnum } from "../../types/enums.js";
import { entityBaseSchema } from "./common.js";

/**
 * Persona frontmatter — mirrors the shape of `packages/db/src/schema.ts`
 * `personas` table, but without tenant/project columns (those are implicit
 * from the `iamu/` directory / `cloud_project_id`).
 *
 * The markdown body below the frontmatter holds the full background narrative.
 */
export const personaFrontmatterSchema = entityBaseSchema.extend({
  name: z.string().min(1),
  role: z.string().min(1),
  /** Short scannable summary; also called `brief` in cloud schema. */
  brief: z.string().nullish(),
  /** Optional structured context (background cues, constraints). */
  context: z.string().nullish(),
  /** Disposition (patient / skeptical / harried / …). */
  disposition: z.string().nullish(),
  /** Free-text cognitive/behavioral description — strengths, limits, friction responses. Consumed by walkthrough prompts as behavioral constraints. */
  cognitive_profile: z.string().nullish(),
  /**
   * Which user this persona is in the product under test — the account,
   * role, or switcher key a walker selects to see what this person sees
   * (e.g. `"marcus"`, `"role: site_manager"`, `"test account forklift-1"`).
   * Read together with the project-level `access` in `context.yml`, which
   * says how switching is done. Never a password or token.
   */
  access: z.string().max(2048).nullish(),
  /** Free-form domain vocabulary the persona uses. */
  domain_vocabulary: z.array(z.unknown()).default([]),
  status: personaStatusEnum.default("active"),
  tags: z.array(z.string()).default([]),
});

export type PersonaFrontmatter = z.infer<typeof personaFrontmatterSchema>;
